import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  TemplateRef,
  computed,
  inject,
  viewChild,
  viewChildren,
} from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { Router } from '@angular/router';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { combineLatest, filter, map, switchMap } from 'rxjs';
import { ApiAsyncPipe, ApiErrorComponent, HeadlineComponent, MapPipe } from '@comparit/core';
import { PageNavigatorService } from '@comparit/shared';
import { BrandingService } from '@comparit/infrastructure';
import type { ErgebnisInput } from '../../interfaces/ergebnis-input.model';
import {
  anyGroupHasDifferences,
  attributHasDifferences,
  groupHasDifferences,
  mergeVergleichGroups,
} from '../../misc/merge-vergleich-groups';
import { ResultsSort } from '../../misc/results-sort.class';
import { ResultsBaseService } from '../../services/results-base.service';
import { VergleichBaseService } from '../../services/vergleich-base.service';
import { CompareBarItemComponent } from '../compare-bar-item/compare-bar-item.component';
import { DownloadPdfButtonComponent } from '../download-pdf-button/download-pdf-button.component';
import { VergleichBoxComponent } from '../vergleich-box/vergleich-box.component';

/**
 * The shared tariff comparison: the marked tariffs as a sticky header, then the backend's attribute
 * groups as rows. Data comes from the app's `VergleichBaseService` and `ResultsBaseService`; this
 * lib names no generated type and no route.
 */
@Component({
  selector: 'ui-vergleich-page',
  templateUrl: './vergleich-page.component.html',
  styleUrl: './vergleich-page.component.scss',
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatIcon,
    MatTooltip,
    ApiAsyncPipe,
    ApiErrorComponent,
    HeadlineComponent,
    MapPipe,
    CompareBarItemComponent,
    DownloadPdfButtonComponent,
    VergleichBoxComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VergleichPageComponent implements OnInit, OnDestroy {
  private readonly navigatorContent = viewChild.required<TemplateRef<unknown>>('navigatorContent');
  /** Header and every group scroll together; both carry `#scroller`. */
  private readonly scrollers = viewChildren<ElementRef<HTMLDivElement>>('scroller');

  private readonly navigator = inject(PageNavigatorService);
  private readonly router = inject(Router);
  private readonly branding = inject(BrandingService);
  protected readonly results: ResultsBaseService<unknown, ErgebnisInput> =
    inject(ResultsBaseService);
  protected readonly vergleich = inject(VergleichBaseService);

  protected readonly showLeistungstexte = new FormControl(true, { nonNullable: true });
  protected readonly showLeistungstexteValue = toSignal(this.showLeistungstexte.valueChanges, {
    initialValue: true,
  });
  protected readonly showOnlyDifferences = new FormControl(false, { nonNullable: true });
  protected readonly showOnlyDifferencesValue = toSignal(this.showOnlyDifferences.valueChanges, {
    initialValue: false,
  });

  private readonly sortBy = toSignal(this.results.calculatorForm.sortBy$);
  private readonly sortDescending = toSignal(this.results.calculatorForm.sortDescending$);
  private readonly resultsSort = new ResultsSort();

  protected readonly bestTarifKombinationId = computed(() =>
    ResultsSort.findBestTarifId(this.results.all().filter(tarif => tarif.successful))
  );

  protected readonly compare$ = combineLatest([
    this.results.calculatorForm.compare$,
    this.results.allResults$,
  ]).pipe(
    map(([compare, results]) => {
      if (!compare || !results) return [];

      const selected = results.filter(result =>
        compare.some(
          item =>
            item.kombinationId === result.kombinationId && item.berechnungId === result.berechnungId
        )
      );

      return this.sortLikeResultsPage(selected).map(({ berechnungId, kombinationId }) => ({
        berechnungId,
        kombinationId,
      }));
    }),
    filter(items => items.length !== 0)
  );

  protected readonly hasMultipleItems$ = this.compare$.pipe(map(items => items.length > 1));

  /**
   * Fetched per compare list rather than combined with it, so a freshly marked tariff never renders
   * against the previous run's attributes.
   */
  protected readonly groups$ = this.compare$.pipe(
    switchMap(kombinationen =>
      this.vergleich
        .fetch$(kombinationen)
        .pipe(map(response => mergeVergleichGroups(kombinationen, response)))
    )
  );

  protected readonly findTarif$ = this.results.findTarif$;
  protected readonly findVersichererLogo$ = this.results.findVersichererLogo$;
  protected readonly attributHasDifferences = attributHasDifferences;
  protected readonly groupHasDifferences = groupHasDifferences;
  protected readonly anyGroupHasDifferences = anyGroupHasDifferences;

  ngOnInit(): void {
    if (!this.results.calculatorForm.ctrls.compare.value.length) {
      void this.router.navigateByUrl(this.vergleich.backUrl);
      return;
    }

    // A reload lands here with the marks restored but no results; this restarts the run.
    this.results.enterCalculatorPage();
    this.navigator.setup.set({
      info: 'Tarifvergleich',
      backLink: { url: this.vergleich.backUrl },
      contentTemplate: this.navigatorContent(),
    });
  }

  ngOnDestroy(): void {
    this.results.leaveCalculatorPage();
  }

  protected openAntrag(tarif: ErgebnisInput): void {
    const { antragUrl } = this.vergleich;
    if (!antragUrl) return;

    void this.router.navigate([antragUrl], {
      queryParams: { berechnungId: tarif.berechnungId, kombinationId: tarif.kombinationId },
    });
  }

  protected syncScroll(source: HTMLDivElement): void {
    const { scrollLeft } = source;
    for (const ref of this.scrollers()) {
      if (ref.nativeElement !== source) ref.nativeElement.scrollLeft = scrollLeft;
    }
  }

  /**
   * The compared tariffs keep the results-page order (the recommendation pinned first when it is
   * part of the selection, then the active sort) so the page and the generated PDF show the same
   * sequence instead of SignalR arrival order (KFZ-2643).
   *
   * Not `ResultsSort.sortWithPinnedRecommendation`: that pins the best of the list it is given,
   * which here would promote a tariff that is not the results page's recommendation.
   */
  private sortLikeResultsPage(selected: ErgebnisInput[]): ErgebnisInput[] {
    const sorted = this.resultsSort.sort(selected, this.sortBy(), this.sortDescending(), true);
    if (!this.branding.config()?.features?.pinRecommendation) return sorted;

    const recommendation = sorted.find(
      tarif => tarif.kombinationId === this.bestTarifKombinationId()
    );
    return recommendation
      ? [recommendation, ...sorted.filter(tarif => tarif !== recommendation)]
      : sorted;
  }
}
